import { CustomEventWithDetail, html, on, View } from 'rune-ts';
import klass from './CalendarView.module.scss';
import { Modal } from '../../atoms/Modal/Modal';
import { endOfWeek, format, getWeekOfMonth, startOfWeek } from 'date-fns';
import startOfMonth from 'date-fns/startOfMonth/index.js';
import endOfMonth from 'date-fns/endOfMonth/index.js';
import { htmlIf } from '../../../../shared/util';
import { ButtonAction } from '../../../../shared/components/atoms/ButtonAction/ButtonAction';
import { typo, typoPcMo } from '../../../../shared/typography/typo';
import { DropdownIcon } from '../../atoms/Icon';
import flatpickr from 'flatpickr';
import { Korean } from 'flatpickr/dist/l10n/ko.js';
import { english } from 'flatpickr/dist/l10n/default.js';
import WeekSelect from 'flatpickr/dist/plugins/weekSelect/weekSelect.js';
import MonthSelect from 'flatpickr/dist/plugins/monthSelect/index.js';

export interface CalendarSelectProps {
  date: Date;
  tab: 'weekly' | 'monthly';
  is_mobile: boolean;
  selector_klass?: string;
  selector_btn_klass?: string;
  hide_period?: boolean;

  max_date?: Date;
  min_date?: Date;
}

export class ChangeDateEvent extends CustomEventWithDetail<{ date: Date }> {}

export class CalendarSelectView extends View<CalendarSelectProps> {
  calendar_modal: Modal;
  picker: any;
  state: {
    start_date: Date;
    end_date: Date;
  };

  static ChangeDateEvent = ChangeDateEvent;

  constructor(props: CalendarSelectProps) {
    super(props);

    this.calendar_modal = new Modal({
      isOpen: false,
      size: 'free',
      is_bottom_sheet: props.is_mobile,
      enable_app_bottom_padding: true,
      full_body_height: props.is_mobile,
      body: this.makeCalendarHtml(),
    });

    this.state =
      props.tab === 'weekly'
        ? {
            start_date: startOfWeek(props.date),
            end_date: endOfWeek(props.date),
          }
        : {
            start_date: startOfMonth(props.date),
            end_date: endOfMonth(props.date),
          };
  }

  makeCalendarHtml() {
    return html`
      <div class="${klass.calendar_wrapper}">
        <div id="date_picker" class="${klass.calendar}"></div>
        <div class="${klass.calendar_btns}">
          ${htmlIf(
            new ButtonAction({
              klass: 'calendar_modal_close_btn',
              label: ET('mps2::modal::취소'),
              size: 'large',
              submit: false,
              type: 'line',
            }),
            !this.data.is_mobile,
          )}
          ${new ButtonAction({
            klass: 'calendar_modal_confirm_btn',
            label: ET('mps2::modal::저장'),
            size: 'large',
            submit: false,
            type: 'blue',
          })}
        </div>
      </div>
    `;
  }

  override template() {
    const { is_mobile, hide_period } = this.data;

    return html`
      <div class="${klass.calendar_selector} ${this.data.selector_klass ?? ''}">
        ${is_mobile && !hide_period ? html.preventEscape(this.makePeriodHtml()) : ''}
        <button
          type="button"
          class="${klass.calendar_btn} ${this.data.selector_btn_klass ?? ''} ${typoPcMo({
            is_mobile: this.data.is_mobile,
            pc: '14_medium',
            mo: '14_medium',
          })}"
        >
          <span class="date_text">${this.makeDateText()}</span> ${DropdownIcon}
        </button>
        ${this.calendar_modal}
      </div>
    `;
  }

  override onMount() {
    const target = this.element().querySelector('#date_picker');
    const is_weekly = this.data.tab === 'weekly';
    const { min_date, max_date } = this.data;

    this.picker = flatpickr(target!, {
      locale: ET.lang === 'kr' ? Korean : english,
      inline: true,
      allowInput: false,
      minDate: min_date || new Date(2020, 2, 1),
      maxDate: max_date || new Date(),
      defaultHour: 0,
      monthSelectorType: 'static',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      plugins: [is_weekly ? new WeekSelect() : new MonthSelect()],
      defaultDate: this.data.date,
    });
  }

  makePeriodHtml() {
    return `
      <span class="${klass.calendar_period} ${typo('12_regular')}">
        ${ET('mps2::creator::ranking::period', {
          date: `${format(this.state.start_date, 'yyyy. M. d')} ~ ${format(this.state.end_date, 'M. d')}`,
        })}
      </span>
    `;
  }

  makeDateText() {
    const tab = this.data.tab;
    return tab === 'weekly'
      ? ET('mps2::creator::ranking::week', {
          year_month: format(this.data.date, ET('mps2::creator::ranking::month')),
          week: getWeekOfMonth(this.data.date),
        })
      : format(new Date(this.data.date), ET('mps2::creator::ranking::month'));
  }

  update() {
    const { tab, date } = this.data;
    this.state =
      tab === 'weekly'
        ? {
            start_date: startOfWeek(date),
            end_date: endOfWeek(date),
          }
        : {
            start_date: startOfMonth(date),
            end_date: endOfMonth(date),
          };

    const period_el = this.element().querySelector(`.${klass.calendar_period}`);
    if (period_el) {
      period_el.outerHTML = this.makePeriodHtml();
    }
    // this.redraw();
  }

  @on('click', `.${klass.calendar_btn}`)
  clickCalendarBtn() {
    this.calendar_modal.open();
  }

  @on('click', '.calendar_modal_close_btn')
  clickCloseModal() {
    this.calendar_modal.close();
  }

  @on('click', '.calendar_modal_confirm_btn')
  clickConfirmModal() {
    const selected_date = this.picker.selectedDates[0];
    const date_el = this.element().querySelector('.date_text');
    this.dispatchEvent(ChangeDateEvent, {
      detail: { date: selected_date },
      bubbles: true,
    });
    this.data.date = selected_date;
    if (date_el) {
      date_el.innerHTML = this.makeDateText();
    }
    this.calendar_modal.close();
  }
}
